import {
  LoginCredentals,
  LoginOAuthCredentals,
  SignupCredentals,
  SignupOAuthCredentals,
} from "@/dtos/Auth.dto";
import { StateObject } from "@/dtos/State.dto";
import {
  ForgotPassword,
  GetUser,
  ResetPassword,
  ResetPasswordObj,
  SetPasswordPasswordAndVerifyObj,
} from "@/dtos/User.dto";
import Auth from "@/services/auth/auth";
import User from "@/services/user";
import {
  CLEAR_ACCOUNT_ISLOADING,
  FORGOT_PASSWORD,
  GET_AUTH_ACCOUNT,
  LOGIN,
  LOGIN_OAUTH,
  LOGOUT,
  REQUEST_EMAIL_VERIFICATION,
  REQUEST_SET_PASSWORD_AND_VERIFY,
  RESET_PASSWORD,
  RESET_PASSWORD_AUTHED,
  SET_PASSWORD_AND_VERIFY,
  SIGNUP,
  SIGNUP_OAUTH,
  UPDATE_ACCOUNT,
  VERIFY_EMAIL,
} from "@/store/action-types";
import {
  SET_ACCOUNT,
  SET_ACCOUNT_ORGANIZATION,
  SET_ERRORMSG,
  SET_MFA_PENDING,
  SET_SESSION_TOKEN,
  SET_REFRESH_TOKEN,
} from "@/store/mutation-types";
import { LOCAL_STORAGE_KEY, IMPERSONATION_SESSION_KEY } from "@/utils/const";
import { CLEAR_IMPERSONATION } from "@/store/mutation-types";
import { isInternalDomain } from "@/utils/internalDomain";
import { clearOnboardingChecklistDismissed } from "@/utils/onboardingChecklistStorage";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const account: Module<any, {}> = {
  state: {
    account: {
      data: null,
      status: null,
      isLoading: false,
    },
    sessionToken: null,
    refreshToken: null,
  },
  getters: {
    account: (state: { account: any }) => state.account,
    sessionToken: (state: { sessionToken: any }) => state.sessionToken,
    refreshToken: (state: { refreshToken: any }) => state.refreshToken,
    isInternalUser: (state: { account: any }) => {
      const user = state.account.data;
      return isInternalDomain(user?.email) || isInternalDomain(user?.organization?.email);
    },
  },
  mutations: {
    [SET_ACCOUNT]: (
      state: { account: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.account.data = payload.data;
      state.account.status = payload.status;
      state.account.isLoading = payload.isLoading;
    },
    [SET_SESSION_TOKEN]: (state: { sessionToken: any }, payload: any) => {
      state.sessionToken = payload;
    },
    [SET_REFRESH_TOKEN]: (state: { refreshToken: any }, payload: any) => {
      state.refreshToken = payload;
    },
    [SET_ACCOUNT_ORGANIZATION]: (state: { account: StateObject }, payload: any) => {
      if (state.account.data) {
        state.account.data.organization = payload;
      }
    },
  },
  actions: {
    [LOGIN]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Auth.login(payload as LoginCredentals);

        if (result) {
          if (result.data.status) {
            if (result.data.mfaRequired) {
              commit(SET_ACCOUNT, { data: null, isLoading: false });
              commit(SET_MFA_PENDING, {
                pendingToken: result.data.pendingToken,
                mfaMethods: result.data.mfaMethods,
              });
              return { mfaRequired: true };
            }

            commit(SET_SESSION_TOKEN, result.data.sessionToken);
            commit(SET_REFRESH_TOKEN, result.data.refreshToken);
            const accountData = {
              ...result.data.data,
              loginMethod: result.data.loginMethod || null,
            };
            commit(SET_ACCOUNT, { data: accountData, isLoading: false });
            clearOnboardingChecklistDismissed(accountData.id);
            return true;
          }
          commit(SET_ACCOUNT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);

          // Extract error message for better UX
          const errorMessage =
            result.data.errorMessage ||
            result.data.message ||
            result.data.error?.errorMessage ||
            result.data.msg ||
            "Login failed. Please try again.";

          // Throw error so component can handle it with toast
          const error = new Error(errorMessage);
          (error as any).response = { data: { errorMessage, message: errorMessage } };
          throw error;
        } else {
          commit(SET_ACCOUNT, { isLoading: false });
          throw new Error("No response from server. Please try again.");
        }
      } catch (error: any) {
        commit(SET_ACCOUNT, { data: null, isLoading: false });

        // Re-throw error so component can handle it
        if (error.response) {
          const errorMessage =
            error.response.data?.errorMessage ||
            error.response.data?.message ||
            error.response.data?.error?.errorMessage ||
            error.message ||
            "Login failed. Please try again.";

          const enhancedError = new Error(errorMessage);
          (enhancedError as any).response = error.response;
          throw enhancedError;
        }

        // Re-throw other errors
        throw error;
      }
    },
    [LOGIN_OAUTH]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Auth.loginOAuth(payload as LoginOAuthCredentals);

        if (result) {
          if (result.data.status) {
            if (result.data.mfaRequired) {
              commit(SET_ACCOUNT, { data: null, isLoading: false });
              commit(SET_MFA_PENDING, {
                pendingToken: result.data.pendingToken,
                mfaMethods: result.data.mfaMethods,
              });
              return { mfaRequired: true };
            }

            commit(SET_SESSION_TOKEN, result.data.sessionToken);
            commit(SET_REFRESH_TOKEN, result.data.refreshToken);
            const accountData = {
              ...result.data.data,
              loginMethod: result.data.loginMethod || null,
            };
            commit(SET_ACCOUNT, { data: accountData, isLoading: false });
            clearOnboardingChecklistDismissed(accountData.id);
            return true;
          }
          commit(SET_ACCOUNT, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ACCOUNT, { isLoading: false });
          return false;
        }
      } catch (error) {
        commit(SET_ACCOUNT, { data: null, isLoading: false });
        throw error;
      }
    },
    [SIGNUP]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      const result: AxiosResponse = await Auth.signup(payload as SignupCredentals);

      if (result) {
        if (result.data.status) {
          commit(SET_SESSION_TOKEN, result.data.sessionToken);
          commit(SET_REFRESH_TOKEN, result.data.refreshToken);
          const accountData = {
            ...result.data.data,
            loginMethod: result.data.loginMethod || null,
          };
          commit(SET_ACCOUNT, { data: accountData, isLoading: false });
          clearOnboardingChecklistDismissed(accountData.id);
          return true;
        }
        commit(SET_ACCOUNT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [SIGNUP_OAUTH]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      const result: AxiosResponse = await Auth.signupOAuth(payload as SignupOAuthCredentals);

      if (result) {
        if (result.data.status) {
          commit(SET_SESSION_TOKEN, result.data.sessionToken);
          commit(SET_REFRESH_TOKEN, result.data.refreshToken);
          const accountData = {
            ...result.data.data,
            loginMethod: result.data.loginMethod || null,
          };
          commit(SET_ACCOUNT, { data: accountData, isLoading: false });
          clearOnboardingChecklistDismissed(accountData.id);
          return true;
        }
        commit(SET_ACCOUNT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [UPDATE_ACCOUNT]: async ({ commit }: any, formData: FormData) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.updateAccount(formData);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [FORGOT_PASSWORD]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.forgotPassword(payload as ForgotPassword);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [RESET_PASSWORD]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.resetPassword(payload as ResetPasswordObj);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [RESET_PASSWORD_AUTHED]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.resetPasswordAuthed(payload as ResetPassword);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [REQUEST_SET_PASSWORD_AND_VERIFY]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.requestSetPasswordAndVerify(payload as GetUser);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return result.data.data;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [SET_PASSWORD_AND_VERIFY]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.setPasswordAndVerify(
        payload as SetPasswordPasswordAndVerifyObj
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [REQUEST_EMAIL_VERIFICATION]: async ({ commit, state }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.requestEmailVerification(payload as GetUser);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [VERIFY_EMAIL]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.verifyEmail(payload as string);

      if (result) {
        if (result.data.status) {
          if (result.data.sessionToken) {
            commit(SET_SESSION_TOKEN, result.data.sessionToken);
          }
          if (result.data.refreshToken) {
            commit(SET_REFRESH_TOKEN, result.data.refreshToken);
          }
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [GET_AUTH_ACCOUNT]: async ({ commit, state }: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await User.getUsers({
        id: state.account.data.id,
      } as GetUser);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data[0], isLoading: false });
          return result.data.data[0];
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [LOGOUT]: async ({ commit, state }: any) => {
      // Revoke server-side while the session token is still attached.
      // POST /auth/logout requires a valid access token (isAuth) to reach
      // the handler at all — if we clear sessionToken first, the request
      // interceptor sends no Authorization header, the backend 401s before
      // ever revoking anything, and the refresh token silently stays valid.
      // classifiedErrorHandler short-circuits on the logout endpoint's own
      // URL, so a failure here can't re-enter the refresh/logout pipeline.
      const refreshTokenToRevoke = state.refreshToken;

      // Best-effort: don't let a network failure (or the 401 above) block
      // local logout — the user must be able to log out either way.
      if (refreshTokenToRevoke) {
        try {
          await Auth.logout(refreshTokenToRevoke);
        } catch {
          // Ignore — local state is cleared below regardless.
        }
      }

      commit(SET_SESSION_TOKEN, null);
      commit(SET_REFRESH_TOKEN, null);
      commit(SET_ACCOUNT, { data: null, isLoading: false });
      commit(CLEAR_IMPERSONATION);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      sessionStorage.removeItem(LOCAL_STORAGE_KEY);
      sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
      return true;
    },
    [CLEAR_ACCOUNT_ISLOADING]: ({ commit }) => {
      commit(SET_ACCOUNT, { isLoading: false });
    },
  },
};

export default account;
